import { Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Link, TableSortLabel, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import CustomPagination from '../../../components/forms/Pagination';
import InfoIcon from '@mui/icons-material/Info';
import { formatDateString } from '../../../utils';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { CreateInvoicesValues, InvoicesContext } from '../contexts/InvoicesContext';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { useNavigate } from 'react-router-dom';

export const InvoiceTable = () => {
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { t, totalRows, invoices, order, orderBy, handleButtonClick, handleNoteButtonClick, handleSort, isLoading, rowSxPropToUse, changePage, page, handleClearAllClick } = useContext(InvoicesContext) as CreateInvoicesValues;
  const navigate = useNavigate();
  const rowsPerPage = 10;

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  const maxPages = () => {
    return Math.ceil(totalRows / rowsPerPage);
  };

  if (invoices.length == 0 && !isLoading) {
    return <EmptyTable bodyRowHeight={50}
      headerHeight={55}
      text={t('title')}
      searchTerm={''}
      onClear={handleClearAllClick}
      onAdd={() => { }}
      showOnAdd={false}
      onAddIsCreating={false}
      addItemText={t('createNewBtn')}
      onAddIsDisabled={false} />;
  }

  return <>
    <TableHeader>
      {t('title')}
      {totalRows > 0 && <Pill text={`${totalRows} ${t('title')}`}></Pill>}
    </TableHeader>
    {isLoading ? <TableLoading headerHeight={55}
      bodyRowHeight={50}
      minTableWidth={1200} /> :
      <TableContainer>
        <Table sx={{ minWidth: '1200px' }}>
          <TableHead >
            <TableRow>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'invoiceNumber'}
                  direction={order}
                  onClick={() => handleSort('invoiceNumber')}>
                  <ClampedText>
                    {t('invoiceNumber')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'orderNumber'}
                  direction={order}
                  onClick={() => handleSort('orderNumber')}>
                  <ClampedText>
                    {t('orderNumber')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'invoiceType'}
                  direction={order}
                  onClick={() => handleSort('invoiceType')}>
                  <ClampedText>
                    {t('invoiceType')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'ewsOrderNumber'}
                  direction={order}
                  onClick={() => handleSort('ewsOrderNumber')}>
                  <ClampedText>
                    {t('ewsOrderNumber')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'invoiceDate'}
                  direction={order}
                  onClick={() => handleSort('invoiceDate')}>
                  <ClampedText>
                    {t('invoiceDate')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'deliveryDate'}
                  direction={order}
                  onClick={() => handleSort('deliveryDate')}>
                  <ClampedText>
                    {t('deliveryDate')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}
                align='right'>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'invoiceTotal'}
                  direction={order}
                  onClick={() => handleSort('invoiceTotal')}>
                  <ClampedText>
                    {t('invoiceTotal')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}
                align='right'>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'receivedValue'}
                  direction={order}
                  onClick={() => handleSort('receivedValue')}>
                  <ClampedText>
                    {t('receivedValue')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}
                align='right'>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'creditValue'}
                  direction={order}
                  onClick={() => handleSort('creditValue')}>
                  <ClampedText>
                    {t('creditValue')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}
                align='right'>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'invoiceValue'}
                  direction={order}
                  onClick={() => handleSort('invoiceValue')}>
                  <ClampedText>
                    {t('actualCost')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}
                align='right'>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'discrepancyValue'}
                  direction={order}
                  onClick={() => handleSort('discrepancyValue')}>
                  <ClampedText>
                    {t('discrepency')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
                <TableSortLabel
                  sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                  active={orderBy === 'status'}
                  direction={order}
                  onClick={() => handleSort('status')}>
                  <ClampedText>
                    {t('status')}
                  </ClampedText>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}
                align='center'>
                <ClampedText>
                  {t('note')}
                </ClampedText>
              </TableCell>
              <TableCell sx={{ minHeight: '55px', maxHeight: '55px', height: '55px' }}>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => <TableRow key={invoice.id}
              sx={rowSxPropToUse(invoice.status ?? '')}>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                <Link href={'/invoice/' + invoice.id}>
                  {invoice.invoiceNumber}
                </Link>
              </TableCell >
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                <Link href={`/create-goods-receipt/${invoice.purchaseOrderHeaderId}`}
                  onClick={(event) => {
                    navigate(`/create-goods-receipt/${invoice.purchaseOrderHeaderId}`);
                    event.stopPropagation();
                    event.preventDefault();
                  }}>
                  {invoice.orderNumber}
                </Link>
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                {invoice.invoiceType}
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                {invoice.ewsOrderNumber}
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                {formatDateString(invoice.invoiceDate ?? '0')}
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                {formatDateString(invoice.deliveryDate ?? '0')}
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}
                align='right'>
                <RoundAndFormatValue value={invoice.invoiceTotal ?? 0}
                  currencyCode={currencyCode} />
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}
                align='right'>
                <RoundAndFormatValue value={invoice.receivedValue ?? 0}
                  currencyCode={currencyCode} />
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}
                align='right'>
                <RoundAndFormatValue value={invoice.creditValue ?? 0}
                  currencyCode={currencyCode} />
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}
                align='right'>
                <RoundAndFormatValue value={invoice.invoiceValue ?? 0}
                  currencyCode={currencyCode} />
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}
                align='right'>
                <RoundAndFormatValue value={invoice.discrepancyValue ?? 0}
                  currencyCode={currencyCode} />
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                {invoice.status}
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}
                align='center'>
                {invoice.note && <Tooltip title={invoice.note}>
                  <IconButton sx={{ p: 0 }}
                    onClick={handleNoteButtonClick(invoice.note ?? '')}>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>}
              </TableCell>
              <TableCell sx={{ minHeight: '50px', maxHeight: '50px' }}>
                <Button size="md"
                  disabled={invoice.status !== 'Failed'
                    || (invoice.note && invoice.note === 'Purchase order not found')
                    || (invoice.note && invoice.note === 'Purchase order not received')
                    || invoice.invoiceType === 'CreditNote'}
                  onClick={handleButtonClick(invoice.id ?? -1)}>
                  {t('accept')}</Button>
              </TableCell>
            </TableRow>)}
            <EmptyRows numberOfCurrentRows={invoices.length ?? 0}
              height={50} />
          </TableBody>
        </Table>
      </TableContainer >}
    <CustomPagination setPage={changePage}
      useNoDataMessage={false}
      page={page}
      maxPages={maxPages()}
      breakpointForChangeDisplay={120000} />
  </>;
};

const ClampedText = (props: { children: string }) => {
  return <Typography
    variant='textXS'
    sx={{
      display: 'block',                 // or 'flex' depending on layout
      overflow: 'hidden',
      maxHeight: 'calc(1em * 2.4)',     // allow 2 lines with some line height margin
      lineHeight: 1.2,                  // control line spacing
      textOverflow: 'clip',             // prevent ellipsis
      wordBreak: 'normal',          // optional: better word wrap
      hyphens: 'auto',                  // optional: for long words
    }}>
    {props.children}
  </Typography>;
};